import { css, html, LitElement } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { api } from '../api.js';
import { EventTask } from '../contexts.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { InputWithStatus } from './input-with-status.js';

export type OrgSlugPicked = {
  orgSlug: string,
}

@customElement('pick-org-name')
export class PickOrgName extends LitElement {

  @query('input-with-status') private _input?: InputWithStatus;

  @property({ attribute: false }) initial?: string;
  @property({ type: Boolean }) disabled?: boolean;

  private _validateOrgSlug = new EventTask(this, 'org-slug', {
    autoRun: false,
    args: () => [this._input?.value()] as const,
    task: async ([orgSlug], { signal }): Promise<OrgSlugPicked> => {
      CHECK(orgSlug !== undefined);
      if (!orgSlug.length) {
        this._input?.setStatus(false, 'Organization name must not be blank');
      } else if (!(/^[.0-9a-z_-]+$/i).test(orgSlug)) {
        this._input?.setStatus(false, 'Organization name can only contain ASCII letters, digits, and the characters ., -, and _.');
      } else if (this.initial && orgSlug.toLowerCase() === this.initial.toLowerCase()) {
        this._input?.setStatus(true, `"${orgSlug}" is available`);
      } else {
        // Small cool-down to not spam server with requests.
        await new Promise(x => setTimeout(x, 200));
        signal.throwIfAborted();
        const status = await api.user.checkNewOrganizationSlug.GET({ orgSlug }, { signal });
        if (status === 'unavailable')
          this._input?.setStatus(false, `"${orgSlug}" is already taken`);
        else if (status === 'reserved')
          this._input?.setStatus(false, `"${orgSlug}" is reserved`);
        else if (status === 'claimable')
          this._input?.setStatus(false, `"${orgSlug}" is not available for self-signup. Please contact support@flakiness.io`);
        else if (status === 'available')
          this._input?.setStatus(true, `"${orgSlug}" is available`);
        else
          this._input?.setStatus(false, `"${orgSlug}" is unavailable`);
      }
      CHECK(this._input?.success());
      return { orgSlug };
    }
  });

  setStatus(success: boolean, message: string) {
    this._input?.setStatus(success, message);
  }

  success(): boolean {
    return this._input?.success() ?? false;
  }

  value(): string {
    return this._input?.value()?.trim() ?? '';
  }

  override render() {
    return html`
      <input-with-status
        placeholder="Organization name"
        autocomplete=off
        ?disabled=${this.disabled}
        initial=${this.initial}
        @sl-input=${() => this._validateOrgSlug.run()}
      ><slot></slot></input-with-status>
    `;
  }

  static styles = [css``];
}
